<template>
  <v-app>
    <v-main class="page-wrapper">
      <v-container fluid class="fill-height d-flex align-center justify-center">
        <v-card elevation="8" rounded="xxl" class="mx-auto account-delete-card">
          <!-- Logo Section -->
          <v-card-text class="text-center pt-6 pb-0">
            <v-avatar size="80" class="mb-2" color="primary lighten-4">
              <v-img
                :src="logoPath"
                alt="Healthy Tantra Logo"
              >
                <template v-slot:placeholder>
                  <v-icon size="50" color="primary">mdi-hospital-box</v-icon>
                </template>
              </v-img>
            </v-avatar>
            <h3 class="text-h6 font-weight-bold mb-1">Healthy Tantra</h3>
          </v-card-text>

          <v-card-title class="font-weight-bold text-h5 justify-center pt-2">
            <v-icon large left color="primary">mdi-account-remove</v-icon>
            Account Delete Request
          </v-card-title>
        <v-divider class="mx-4"></v-divider>
        <v-card-text>
          <v-form ref="form" v-model="isFormValid" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Full Name *"
                  prepend-inner-icon="mdi-account"
                  :rules="nameRules"
                  :error-messages="errors.name"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.phone"
                  outlined
                  label="Phone Number *"
                  prepend-inner-icon="mdi-phone"
                  :rules="phoneRules"
                  :error-messages="errors.phone"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
          
          <v-divider class="my-4"></v-divider>
          
          <div class="text-center">
            <v-btn
              :loading="isLoading"
              :disabled="!isFormValid"
              rounded
              color="primary"
              size="large"
              @click="submitRequest"
              class="px-8"
            >
              <v-icon left>mdi-send</v-icon>
              Send Request
            </v-btn>
            <v-btn
              rounded
              color="grey"
              text
              class="ml-3"
              @click="handleCancel"
            >
              Cancel
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" max-width="450" persistent>
        <v-card rounded="xxl">
          <v-card-text class="text-center py-6">
            <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
            <h2 class="text-h5 font-weight-bold success--text mb-2">Success!</h2>
            <p class="text-body-1 mb-4">Account delete request has been submitted successfully.</p>
            <v-btn
              color="success"
              rounded
              @click="handleSuccess"
              class="px-6"
            >
              OK
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Error Dialog -->
      <v-dialog v-model="errorDialog.show" max-width="450" persistent>
        <v-card rounded="xxl">
          <v-card-text class="text-center py-6">
            <v-icon size="80" color="error" class="mb-4">mdi-alert-circle</v-icon>
            <h2 class="text-h5 font-weight-bold error--text mb-2">Error!</h2>
            <p class="text-body-1 mb-4">{{ errorDialog.message }}</p>
            <v-btn
              color="error"
              rounded
              @click="errorDialog.show = false"
              class="px-6"
            >
              OK
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";

export default {
  name: "AccountDeleteRequestCreate",
  data() {
    return {
      isFormValid: false,
      logoPath: require('../../assets/healthy-tantra-logo.png'),
      form: {
        name: "",
        phone: "",
        status: "1", // Default to Pending
      },
      isLoading: false,
      successDialog: false,
      errorDialog: {
        show: false,
        message: "",
      },
      errors: {},
      nameRules: [
        v => !!v || "Name is required",
        v => (v && v.length >= 2) || "Name must be at least 2 characters",
        v => (v && v.length <= 100) || "Name must be less than 100 characters",
      ],
      phoneRules: [
        v => !!v || "Phone number is required",
        v => (v && v.length >= 10) || "Phone number must be at least 10 digits",
        v => (v && v.length <= 15) || "Phone number must be less than 15 digits",
        v => /^[\d+\-\s()]+$/.test(v) || "Phone number must contain only digits, +, -, spaces, and parentheses",
      ],
    };
  },
  methods: {
    async submitRequest() {
      // Validate form
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isLoading = true;
      this.errors = {};

      try {
        await axios.post("/account_delete_request", this.form);
        this.successDialog = true;
        this.resetForm();
      } catch (error) {
        console.error("Error creating account delete request:", error);
        
        if (error.response && error.response.status === 422) {
          // Validation errors
          this.errors = error.response.data.errors || {};
          this.errorDialog.message = "Please check the form for validation errors.";
        } else {
          this.errorDialog.message = "An unexpected error occurred. Please try again.";
        }
        this.errorDialog.show = true;
      } finally {
        this.isLoading = false;
      }
    },
    
    resetForm() {
      this.form = {
        name: "",
        phone: "",
        status: "1",
      };
      this.errors = {};
      this.$refs.form.resetValidation();
    },
    
    handleSuccess() {
      this.successDialog = false;
      // Navigate based on authentication status
      const isAuthenticated = this.$store.getters["auth/authenticated"];
      if (isAuthenticated) {
        this.$router.push("/account-delete-requests");
      } else {
        this.$router.push("/login");
      }
    },

    handleCancel() {
      const isAuthenticated = this.$store.getters["auth/authenticated"];
      if (isAuthenticated) {
        this.$router.go(-1);
      } else {
        this.$router.push("/login");
      }
    },
  },
  mounted() {
    // Auto-focus on name field when component loads
    this.$nextTick(() => {
      const nameField = this.$el.querySelector('input[aria-label="Full Name *"]');
      if (nameField) {
        nameField.focus();
      }
    });
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.v-card {
  margin-top: 20px;
  margin-bottom: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2) !important;
  background: #ffffff !important;
}

.account-delete-card {
  width: 40%;
  max-width: 1200px;
}

.v-card-title {
  background: linear-gradient(to right, #f5f7fa, #ffffff);
}

.v-form {
  max-width: 100%;
}

.success--text {
  color: #4caf50 !important;
}

.error--text {
  color: #f44336 !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .page-wrapper {
    padding: 10px;
  }

  .v-card {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .account-delete-card {
    width: 95%;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .account-delete-card {
    width: 80%;
  }
}
</style>